import { useState, useRef, useCallback, useEffect } from 'react';
import { ImageCropModal } from '../image-editor';
import { ImageViewer } from '../components';
import type { QuestionImageSlot } from '@padcanvas/shared';
import { useQuestionSource } from './questionSource';

interface ImageUploadFieldProps {
  label: string;
  value: string | null | undefined;
  onChange: (url: string | null) => void;
  maxPreviewHeight?: number;
  onUploadImage: (base64: string, mimeType: string) => Promise<{ url: string }>;
  /** Which image this is, so it can be cut from the question's source paper.
   *  Omitted ⇒ no "from paper" action. */
  sourceSlot?: QuestionImageSlot;
}

type UploadStatus = 'idle' | 'uploading' | 'error';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function ImageUploadField({ label, value, onChange, maxPreviewHeight = 200, onUploadImage, sourceSlot }: ImageUploadFieldProps) {
  const source = useQuestionSource();
  const fromPaper = source && sourceSlot ? () => source.openCrop(sourceSlot) : undefined;
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);

  // Auto-focus the zone when it appears so paste works immediately
  useEffect(() => {
    if (showUploadZone) zoneRef.current?.focus();
  }, [showUploadZone]);

  const uploadImage = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setStatus('error');
      setErrorMsg('Not an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setStatus('error');
      setErrorMsg('Image too large (max 10MB)');
      return;
    }

    setStatus('uploading');
    setErrorMsg('');
    try {
      const base64 = await fileToBase64(file);
      const { url } = await onUploadImage(base64, file.type);
      onChange(url);
      setStatus('idle');
      setShowUploadZone(false);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Upload failed');
    }
  }, [onChange, onUploadImage]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) uploadImage(file);
        return;
      }
    }
  }, [uploadImage]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      uploadImage(file);
    }
  }, [uploadImage]);

  return (
    <div className="form-section">
      <label>{label}</label>
      <div className="image-upload-url-row">
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value || null)}
          placeholder="https://... or paste/drop image"
        />
        <button
          type="button"
          className="image-upload-toggle-btn"
          onClick={() => setShowUploadZone(!showUploadZone)}
        >
          Update Image
        </button>
        {fromPaper && !value && (
          <button type="button" className="image-upload-toggle-btn" onClick={fromPaper}>
            Add from paper
          </button>
        )}
      </div>

      {showUploadZone && (
        <div
          ref={zoneRef}
          className={`image-upload-zone ${status === 'uploading' ? 'uploading' : ''}`}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => { if (status !== 'uploading') fileInputRef.current?.click(); }}
          tabIndex={0}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) uploadImage(file);
              e.target.value = '';
            }}
            style={{ display: 'none' }}
          />
          <span className="image-upload-zone-text">
            {status === 'uploading' ? 'Uploading...' : 'Paste, drop, or click to upload image'}
          </span>
        </div>
      )}

      {status === 'error' && (
        <span className="image-upload-error">{errorMsg}</span>
      )}

      {value && (
        <div className="image-upload-preview" style={{ maxHeight: maxPreviewHeight }}>
          <ImageViewer src={value} alt={label} style={{ maxHeight: maxPreviewHeight }} />
          <div className="image-upload-preview-actions">
            <button
              type="button"
              className="sui-image-crop-trigger"
              onClick={() => setShowCrop(true)}
              title="Crop image"
            >
              Crop
            </button>
            {fromPaper && (
              <button
                type="button"
                className="sui-image-crop-trigger"
                onClick={fromPaper}
                title="Re-cut this image from the page it was extracted from"
              >
                Crop from paper
              </button>
            )}
            <button
              type="button"
              className="image-upload-remove"
              onClick={() => onChange(null)}
              title="Remove image"
            >
              x
            </button>
          </div>
        </div>
      )}

      {showCrop && value && (
        <ImageCropModal
          imageUrl={value}
          onCropped={url => { onChange(url); setShowCrop(false); }}
          onClose={() => setShowCrop(false)}
        />
      )}
    </div>
  );
}
